/**
 * SocialTopics — /social/topics
 * Hashtag'leri konu başlıkları olarak gösterir.
 * Sol: konu listesi. Sağ: seçili konunun postları + yorumları.
 */
import { Hash, MessageCircle, RefreshCw, TrendingUp } from 'lucide-react'
import { useEffect, useState } from 'react'
import MainLayout from '../components/layout/MainLayout'
import PostCard from '../components/social/PostCard'
import type { Post } from '../services/socialApi'
import { getPosts, getTrendingHashtags } from '../services/socialApi'

interface Topic {
  tag: string
  post_count: number
  title: string
}

function tagToTitle(tag: string): string {
  return tag
    .replace(/([A-Z])/g, ' $1')
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ')
    .trim()
}

export default function SocialTopicsPage() {
  const [topics, setTopics] = useState<Topic[]>([])
  const [selected, setSelected] = useState<Topic | null>(null)
  const [posts, setPosts] = useState<Post[]>([])
  const [loading, setLoading] = useState(true)
  const [postsLoading, setPostsLoading] = useState(false)

  const loadTopics = async () => {
    setLoading(true)
    try {
      const hashtags = await getTrendingHashtags(40)
      const rows: Topic[] = hashtags.map(h => ({
        tag: h.tag,
        post_count: h.post_count,
        title: tagToTitle(h.tag),
      }))
      setTopics(rows)
      if (!selected && rows[0]) selectTopic(rows[0])
    } catch {
      setTopics([])
    } finally {
      setLoading(false)
    }
  }

  const selectTopic = async (topic: Topic) => {
    setSelected(topic)
    setPostsLoading(true)
    try {
      const data = await getPosts({ hashtag: topic.tag, per_page: 30 })
      setPosts(data.posts)
    } catch {
      setPosts([])
    } finally {
      setPostsLoading(false)
    }
  }

  useEffect(() => { void loadTopics() }, [])

  return (
    <MainLayout>
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '24px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Hash size={22} color="#667eea" /> Topics
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-tertiary)' }}>
              Browse discussions by topic
            </p>
          </div>
          <button onClick={() => void loadTopics()} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px',
            borderRadius: 8, border: '1px solid var(--border-color)', background: 'var(--card-bg)',
            color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
          }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 20, alignItems: 'start' }}>

          {/* LEFT: Topic list */}
          <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-color)', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 5 }}>
              <TrendingUp size={11} /> TRENDING TOPICS
            </div>
            {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>Loading...</div>}
            {!loading && topics.length === 0 && (
              <div style={{ padding: 16, fontSize: 12, color: 'var(--text-muted)' }}>No topics yet</div>
            )}
            <div style={{ maxHeight: 600, overflowY: 'auto' }}>
              {topics.map(topic => {
                const active = selected?.tag === topic.tag
                return (
                  <button key={topic.tag} onClick={() => void selectTopic(topic)} style={{
                    width: '100%', padding: '10px 14px', textAlign: 'left',
                    background: active ? '#667eea12' : 'transparent',
                    border: 'none', borderBottom: '1px solid var(--border-color)',
                    borderLeft: `3px solid ${active ? '#667eea' : 'transparent'}`,
                    cursor: 'pointer', transition: 'all 0.12s',
                  }}>
                    <div style={{ fontSize: 12, fontWeight: active ? 700 : 500, color: active ? '#667eea' : 'var(--text-primary)' }}>
                      {topic.title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>#{topic.tag}</span>
                      <span style={{ fontSize: 10, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <MessageCircle size={9} /> {topic.post_count}
                      </span>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>

          {/* RIGHT: Posts */}
          <div>
            {selected && (
              <div style={{ marginBottom: 14, padding: '12px 16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                <Hash size={16} color="#667eea" />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{selected.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>#{selected.tag} · {selected.post_count} posts</div>
                </div>
              </div>
            )}

            {postsLoading && (
              <div style={{ textAlign: 'center', padding: 40, color: '#667eea', fontSize: 13 }}>Loading posts...</div>
            )}

            {!postsLoading && !selected && (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 13 }}>
                Select a topic to see posts
              </div>
            )}

            {!postsLoading && selected && posts.length === 0 && (
              <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)', fontSize: 13 }}>
                No posts for #{selected.tag} yet
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {posts.map(post => <PostCard key={post.id} post={post} />)}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  )
}
